/**
 * TriggerEditFormBody — shared form body for CREATE / ALTER TRIGGER.
 *
 * Engines:
 *   - mssql:    timing limited to AFTER / INSTEAD OF (no BEFORE). Multi-event
 *               OK. Body opaque text, with leading "AS " expected.
 *   - postgres: full timing surface. Body lives in a separately-defined
 *               function (pgsqlExecuteFunction). Granularity ROW or STATEMENT.
 *   - mysql:    timing limited to BEFORE / AFTER (no INSTEAD OF). Single
 *               event only. Body opaque text.
 *   - oracle:   full timing surface. Body opaque PL/SQL text.
 *               OR REPLACE + EDITIONABLE supported.
 *   - sqlite:   full timing surface. Single event only. TEMP supported.
 *
 * Modes:
 *   - `create`: builds a `TriggerShellDef` and dispatches `commitCreateTrigger`.
 *   - `alter`:  builds a `TriggerChange` (enable/disable) and dispatches
 *               `commitAlterTrigger`. mysql / sqlite render the alter mode
 *               disabled with the drop-and-recreate redirect.
 *
 * The owning popover seeds `initialValues` from the parked statement.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice D6 — Trigger shell UI.
 */
import { useState } from 'react';

import { Button } from '../../../../../components/ui/button';
import type { QualifiedName } from '../../../../sql-grammar/model';
import {
  commitAlterTrigger,
  getAlterTriggerUnsupportedTooltip,
  isAlterTriggerSupported,
  validateAlterTriggerForEngine,
} from '../../edit/edits/alterTrigger';
import {
  commitCreateTrigger,
  isBeforeTimingSupported,
  isInsteadOfTimingSupported,
  isMultiEventSupported,
  validateCreateTriggerForEngine,
} from '../../edit/edits/createTrigger';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { TriggerFormFields } from './TriggerFormFields';
import {
  buildTriggerShellDef,
  DEFAULT_TRIGGER_FORM_VALUES,
  type TriggerEditMode,
  type TriggerFormValues,
} from './triggerFormHelpers';
import { FormActionsSlot } from './FormActionsSlot';
import { FormEngineChip } from './FormEngineChip';

export type { TriggerEditMode, TriggerFormValues } from './triggerFormHelpers';

export interface TriggerEditFormBodyProps {
  readonly editContext: VqeDdlEditContext;
  readonly mode: TriggerEditMode;
  readonly initialValues?: Partial<TriggerFormValues>;
  readonly testId: string;
  readonly onCommitted?: () => void;
  readonly onCancel?: () => void;
}

export function TriggerEditFormBody(props: TriggerEditFormBodyProps) {
  const { editContext, mode, initialValues, testId, onCommitted, onCancel } = props;
  const engine = editContext.service.engineId;

  const [values, setValues] = useState<TriggerFormValues>({
    ...DEFAULT_TRIGGER_FORM_VALUES,
    ...initialValues,
  });
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  if (mode === 'alter' && !isAlterTriggerSupported(engine)) {
    return renderAlterUnsupported(testId, engine, onCancel);
  }

  if (mode === 'create' && engine === 'mssql' && values.timing === 'before' && !isBeforeTimingSupported(engine)) {
    // mssql: shouldn't be reachable since we initialise to 'after', but guard
    // against parked statements with mssql + BEFORE coming through.
  }

  async function handleSubmit() {
    setError(null);
    if (mode === 'create') {
      const trigger = buildTriggerShellDef(engine, values);
      const validationError = validateCreateTriggerForEngine(engine, { trigger });
      if (validationError !== null) {
        setError(validationError);
        return;
      }
      setPending(true);
      try {
        await commitCreateTrigger(editContext, { trigger });
        onCommitted?.();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        setPending(false);
      }
      return;
    }
    const triggerName: QualifiedName = {
      name: values.name.trim(),
      raw: values.name.trim(),
      quoted: false,
      foldedName: values.name.trim().toLowerCase(),
    };
    const target: QualifiedName | undefined = values.target.trim().length > 0 && engine !== 'oracle'
      ? {
          name: values.target.trim(), raw: values.target.trim(), quoted: false,
          foldedName: values.target.trim().toLowerCase(),
        }
      : undefined;
    const args = {
      triggerName,
      ...(target ? { target } : {}),
      change: { enabled: values.enabled },
    };
    const validationError = validateAlterTriggerForEngine(engine, args);
    if (validationError !== null) {
      setError(validationError);
      return;
    }
    setPending(true);
    try {
      await commitAlterTrigger(editContext, args);
      onCommitted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPending(false);
    }
  }

  const submitLabel = pending
    ? 'Saving…'
    : mode === 'create' ? 'Create trigger' : (values.enabled ? 'Enable trigger' : 'Disable trigger');

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold">
          {mode === 'create' ? 'Create trigger' : 'Alter trigger'}
        </div>
        <FormEngineChip engine={engine} />
      </div>

      <TriggerFormFields
        engine={engine}
        mode={mode}
        values={values}
        setValues={setValues}
        testId={testId}
        canBefore={isBeforeTimingSupported(engine)}
        canInsteadOf={isInsteadOfTimingSupported(engine)}
        multiEventSupported={isMultiEventSupported(engine)}
      />

      {error ? (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive"
          data-testid={`${testId}-error`}
        >
          {error}
        </div>
      ) : null}

      <FormActionsSlot className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onCancel?.()}
          data-testid={`${testId}-cancel`}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={pending}
          onClick={() => void handleSubmit()}
          data-testid={`${testId}-submit`}
        >
          {submitLabel}
        </Button>
      </FormActionsSlot>
    </div>
  );
}

function renderAlterUnsupported(
  testId: string, engine: string, onCancel?: () => void,
) {
  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <div className="text-xs font-semibold">Alter trigger</div>
      <div
        className="rounded-md border border-border bg-muted/40 px-2 py-1 text-[10px] text-muted-foreground"
        data-testid={`${testId}-unsupported`}
      >
        {getAlterTriggerUnsupportedTooltip(engine as never)}
      </div>
      <div className="flex items-center justify-end">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onCancel?.()}
          data-testid={`${testId}-close`}
        >
          Close
        </Button>
      </div>
    </div>
  );
}
